/* eslint-disable react-hooks/exhaustive-deps */
import currency from "currency.js";
import { useMemo } from "react";
import { useSortBy, useTable } from "react-table";
import Collapse from "antd/es/collapse/Collapse";
import { COLORS, TEXT_COLORS } from "../../../constants/design/colors";
import { IAevoPosition } from "../../../interfaces/aevo";
import { DefaultCellForColumn } from "../../Table/DefaultCellForColumn";
import { DefaultHeaderForColumn } from "../../Table/DefaultHeaderForColumn";
import { ITableColumn } from "../../Table/TableColumn";
import { Align, MinifiedTableWrapper, TableHeaderCell, TableLoader } from "../../Table/style";
import { Spinner } from "../../shared/Spinner";
import { IBinancePosition } from "../../../interfaces/binance";
import { IBybitPosition } from "../../../interfaces/bybit";
import { IHyperliquidPosition } from "../../../interfaces/hyperliquid";
import { ILighterPosition } from "../../../interfaces/lighter";
import { IAggregatedPosition, IPerpAggregatedPosition } from "../../../interfaces/positions";
import { standardizeAevoPositions } from "../../../utils/positions/aevo";
import { standardizeBinancePositions } from "../../../utils/positions/binance";
import { standardizeBybitPositions } from "../../../utils/positions/bybit";
import { standardizeHyperliquidPositions } from "../../../utils/positions/hyperliquid";
import { standardizeLighterPositions } from "../../../utils/positions/lighter";
import { useMarkets } from "../../../hooks/api/aevo/useMarkets";
import { retrieveAssetByInstrumentName } from "../../../utils/strings";
import CollapseHeaderCount from "../../TableCollapse";

// Sort function for perp positions
const sortPerpPositions = (positions: IPerpAggregatedPosition[]) => positions.sort((a, b) => {
  // Compare by contracts
  if (a.contracts > 0 && b.contracts <= 0) return -1;
  if (a.contracts < 0 && b.contracts > 0) return 1;
  if (a.contracts === 0 && b.contracts !== 0) return 1;
  if (b.contracts === 0 && a.contracts !== 0) return -1;

  return 0;
});

// Helper function to get color for delta values
const getDeltaColor = (value: number) => {
  if (value > 0) return COLORS.green.one;
  if (value < 0) return COLORS.red.one;
  return TEXT_COLORS.one;
};

interface IPerpAggregatedPositionTableProps {
  selectedAsset?: string;
  isLoading?: boolean;
  instrumentFilter?: string;
  deltaDisplayUnit?: "usd" | "contracts";

  // Used in Positions.tsx
  aevoPositions?: IAevoPosition[];
  binancePositions?: IBinancePosition[];
  bybitPositions?: IBybitPosition[];
  hyperliquidPositions?: IHyperliquidPosition[];
  lighterPositions?: ILighterPosition[];

  // Used in Books.tsx
  aggregatedAevoPositions?: IAggregatedPosition[];
  aggregatedBinancePositions?: IAggregatedPosition[];
  aggregatedBybitPositions?: IAggregatedPosition[];
  aggregatedHyperliquidPositions?: IAggregatedPosition[];
  aggregatedLighterPositions?: IAggregatedPosition[];
  aggregatedLighterMainnetPositions?: IAggregatedPosition[];
}

export function PerpAggregatedPositionTable({
  aevoPositions = [],
  binancePositions = [],
  bybitPositions = [],
  hyperliquidPositions = [],
  lighterPositions = [],
  aggregatedAevoPositions,
  aggregatedBinancePositions,
  aggregatedBybitPositions,
  aggregatedHyperliquidPositions,
  aggregatedLighterPositions,
  aggregatedLighterMainnetPositions,
  selectedAsset,
  isLoading,
  instrumentFilter = "",
  deltaDisplayUnit = "usd",
}: IPerpAggregatedPositionTableProps) {
  const { data: marketsData } = useMarkets();
  const isContractView = deltaDisplayUnit === "contracts";

  const aevo =
    aevoPositions.length > 0 ? standardizeAevoPositions(aevoPositions, selectedAsset) : aggregatedAevoPositions || [];
  const binance =
    binancePositions.length > 0 ? standardizeBinancePositions(binancePositions) : aggregatedBinancePositions || [];
  const bybit =
    bybitPositions.length > 0 ? standardizeBybitPositions(bybitPositions) : aggregatedBybitPositions || [];
  const hyperliquid =
    hyperliquidPositions.length > 0 ? standardizeHyperliquidPositions(hyperliquidPositions) : aggregatedHyperliquidPositions || [];
  const lighter =
    lighterPositions.length > 0 ? standardizeLighterPositions(lighterPositions) : aggregatedLighterPositions || [];

  const lighterMainnet = aggregatedLighterMainnetPositions || [];

  const memoizedPositions = useMemo(() => {
    // Create a map of instrument_name to exchange positions
    const positionMap: { [key: string]: IPerpAggregatedPosition } = {};

    // Seed every perpetual market so flat/zero assets still appear in the table.
    // Venue positions below overlay their deltas onto these baseline rows.
    marketsData?.forEach((market) => {
      const instrumentName = `${market.underlying_asset}-PERPETUAL`;
      if (!positionMap[instrumentName]) {
        positionMap[instrumentName] = {
          instrument_name: instrumentName,
          asset: market.underlying_asset,
          contracts: 0,
          pnl: 0,
          side: "-",
          aevoDelta: 0,
          binanceDelta: 0,
          bybitDelta: 0,
          hyperliquidDelta: 0,
          lighterDelta: 0,
          lighterMainnetDelta: 0,
          netDelta: 0,
          aevoMarkPrice: 0,
          binanceMarkPrice: 0,
          bybitMarkPrice: 0,
          hyperliquidMarkPrice: 0,
          lighterMarkPrice: 0,
          lighterMainnetMarkPrice: 0,
          markPrice: Number(market.mark_price) || 0,
        };
      }
    });

    // Process Aevo positions
    aevo.forEach((pos) => {
      const asset = retrieveAssetByInstrumentName(pos.instrument_name);
      const market = marketsData?.find((m) => m.underlying_asset === asset);
      const markPrice = market ? Number(market.mark_price) : 0;

      if (!positionMap[pos.instrument_name]) {
        positionMap[pos.instrument_name] = {
          instrument_name: pos.instrument_name,
          asset: pos.asset,
          contracts: 0,
          pnl: 0,
          side: "-",
          aevoDelta: 0,
          binanceDelta: 0,
          bybitDelta: 0,
          hyperliquidDelta: 0,
          lighterDelta: 0,
          lighterMainnetDelta: 0,
          netDelta: 0,
          aevoMarkPrice: 0,
          binanceMarkPrice: 0,
          bybitMarkPrice: 0,
          hyperliquidMarkPrice: 0,
          lighterMarkPrice: 0,
          lighterMainnetMarkPrice: 0,
          markPrice,
        };
      }
      positionMap[pos.instrument_name].aevoDelta = pos.contracts;
      positionMap[pos.instrument_name].aevoMarkPrice = markPrice;
      positionMap[pos.instrument_name].contracts += pos.contracts;
      positionMap[pos.instrument_name].pnl += pos.pnl || 0;
    });

    // Process Binance positions
    binance.forEach((pos) => {
      const asset = retrieveAssetByInstrumentName(pos.instrument_name);
      const market = marketsData?.find((m) => m.underlying_asset === asset);
      const markPrice = market ? Number(market.mark_price) : 0;

      if (!positionMap[pos.instrument_name]) {
        positionMap[pos.instrument_name] = {
          instrument_name: pos.instrument_name,
          asset: pos.asset,
          contracts: 0,
          pnl: 0,
          side: "-",
          aevoDelta: 0,
          binanceDelta: 0,
          bybitDelta: 0,
          hyperliquidDelta: 0,
          lighterDelta: 0,
          lighterMainnetDelta: 0,
          netDelta: 0,
          aevoMarkPrice: 0,
          binanceMarkPrice: 0,
          bybitMarkPrice: 0,
          hyperliquidMarkPrice: 0,
          lighterMarkPrice: 0,
          lighterMainnetMarkPrice: 0,
          markPrice,
        };
      }
      positionMap[pos.instrument_name].binanceDelta = pos.contracts;
      positionMap[pos.instrument_name].binanceMarkPrice = markPrice;
      positionMap[pos.instrument_name].contracts += pos.contracts;
      positionMap[pos.instrument_name].pnl += pos.pnl || 0;
    });

    // Process Bybit positions
    bybit.forEach((pos) => {
      const asset = retrieveAssetByInstrumentName(pos.instrument_name);
      const market = marketsData?.find((m) => m.underlying_asset === asset);
      const markPrice = market ? Number(market.mark_price) : 0;

      if (!positionMap[pos.instrument_name]) {
        positionMap[pos.instrument_name] = {
          instrument_name: pos.instrument_name,
          asset: pos.asset,
          contracts: 0,
          pnl: 0,
          side: "-",
          aevoDelta: 0,
          binanceDelta: 0,
          bybitDelta: 0,
          hyperliquidDelta: 0,
          lighterDelta: 0,
          lighterMainnetDelta: 0,
          netDelta: 0,
          aevoMarkPrice: 0,
          binanceMarkPrice: 0,
          bybitMarkPrice: 0,
          hyperliquidMarkPrice: 0,
          lighterMarkPrice: 0,
          lighterMainnetMarkPrice: 0,
          markPrice,
        };
      }
      positionMap[pos.instrument_name].bybitDelta = pos.contracts;
      positionMap[pos.instrument_name].bybitMarkPrice = markPrice;
      positionMap[pos.instrument_name].contracts += pos.contracts;
      positionMap[pos.instrument_name].pnl += pos.pnl || 0;
    });

    // Process Hyperliquid positions
    hyperliquid.forEach((pos) => {
      const asset = retrieveAssetByInstrumentName(pos.instrument_name);
      const market = marketsData?.find((m) => m.underlying_asset === asset);
      const markPrice = market ? Number(market.mark_price) : 0;

      if (!positionMap[pos.instrument_name]) {
        positionMap[pos.instrument_name] = {
          instrument_name: pos.instrument_name,
          asset: pos.asset,
          contracts: 0,
          pnl: 0,
          side: "-",
          aevoDelta: 0,
          binanceDelta: 0,
          bybitDelta: 0,
          hyperliquidDelta: 0,
          lighterDelta: 0,
          lighterMainnetDelta: 0,
          netDelta: 0,
          aevoMarkPrice: 0,
          binanceMarkPrice: 0,
          bybitMarkPrice: 0,
          hyperliquidMarkPrice: 0,
          lighterMarkPrice: 0,
          lighterMainnetMarkPrice: 0,
          markPrice,
        };
      }
      positionMap[pos.instrument_name].hyperliquidDelta = pos.contracts;
      positionMap[pos.instrument_name].hyperliquidMarkPrice = pos.hyperliquidMarkPrice || markPrice;
      positionMap[pos.instrument_name].contracts += pos.contracts;
      positionMap[pos.instrument_name].pnl += pos.pnl || 0;
    });

    // Process Lighter positions
    lighter.forEach((pos) => {
      const asset = retrieveAssetByInstrumentName(pos.instrument_name);
      const market = marketsData?.find((m) => m.underlying_asset === asset);
      const markPrice = market ? Number(market.mark_price) : pos.lighterMarkPrice || 0;

      if (!positionMap[pos.instrument_name]) {
        positionMap[pos.instrument_name] = {
          instrument_name: pos.instrument_name,
          asset: pos.asset,
          contracts: 0,
          pnl: 0,
          side: "-",
          aevoDelta: 0,
          binanceDelta: 0,
          bybitDelta: 0,
          hyperliquidDelta: 0,
          lighterDelta: 0,
          lighterMainnetDelta: 0,
          netDelta: 0,
          aevoMarkPrice: 0,
          binanceMarkPrice: 0,
          bybitMarkPrice: 0,
          hyperliquidMarkPrice: 0,
          lighterMarkPrice: 0,
          lighterMainnetMarkPrice: 0,
          markPrice,
        };
      }
      if (!positionMap[pos.instrument_name].markPrice && pos.lighterMarkPrice) {
        positionMap[pos.instrument_name].markPrice = pos.lighterMarkPrice;
      }
      positionMap[pos.instrument_name].lighterDelta = pos.contracts;
      positionMap[pos.instrument_name].lighterMarkPrice = pos.lighterMarkPrice || markPrice;
      positionMap[pos.instrument_name].contracts += pos.contracts;
      positionMap[pos.instrument_name].pnl += pos.pnl || 0;
    });

    // Process Lighter mainnet positions
    lighterMainnet.forEach((pos) => {
      const asset = retrieveAssetByInstrumentName(pos.instrument_name);
      const market = marketsData?.find((m) => m.underlying_asset === asset);
      const markPrice = market ? Number(market.mark_price) : pos.lighterMainnetMarkPrice || 0;

      if (!positionMap[pos.instrument_name]) {
        positionMap[pos.instrument_name] = {
          instrument_name: pos.instrument_name,
          asset: pos.asset,
          contracts: 0,
          pnl: 0,
          side: "-",
          aevoDelta: 0,
          binanceDelta: 0,
          bybitDelta: 0,
          hyperliquidDelta: 0,
          lighterDelta: 0,
          lighterMainnetDelta: 0,
          netDelta: 0,
          aevoMarkPrice: 0,
          binanceMarkPrice: 0,
          bybitMarkPrice: 0,
          hyperliquidMarkPrice: 0,
          lighterMarkPrice: 0,
          lighterMainnetMarkPrice: 0,
          markPrice,
        };
      }
      if (!positionMap[pos.instrument_name].markPrice && pos.lighterMainnetMarkPrice) {
        positionMap[pos.instrument_name].markPrice = pos.lighterMainnetMarkPrice;
      }
      positionMap[pos.instrument_name].lighterMainnetDelta = pos.contracts;
      positionMap[pos.instrument_name].lighterMainnetMarkPrice = pos.lighterMainnetMarkPrice || markPrice;
      positionMap[pos.instrument_name].contracts += pos.contracts;
      positionMap[pos.instrument_name].pnl += pos.pnl || 0;
    });

    // Calculate net delta and side for each position
    const positions = Object.values(positionMap).map((pos) => {
      const netDelta =
        pos.aevoDelta +
        pos.binanceDelta +
        pos.bybitDelta +
        pos.hyperliquidDelta +
        pos.lighterDelta +
        pos.lighterMainnetDelta;
      let side = "-";
      if (pos.contracts > 0) {
        side = "buy";
      } else if (pos.contracts < 0) {
        side = "sell";
      }

      return {
        ...pos,
        netDelta,
        side,
      };
    });

    let filteredPositions = positions;

    if (selectedAsset) {
      filteredPositions = filteredPositions.filter((pos) => pos.asset === selectedAsset);
    }

    if (instrumentFilter) {
      const filterLower = instrumentFilter.toLowerCase();
      filteredPositions = filteredPositions.filter((pos) =>
        pos.asset.toLowerCase().includes(filterLower)
      );
    }

    return sortPerpPositions(filteredPositions);
  }, [aevo, binance, bybit, hyperliquid, lighter, lighterMainnet, selectedAsset, marketsData, instrumentFilter]);

  const columns: ITableColumn<IPerpAggregatedPosition>[] = useMemo(
    () => {
      const formatDeltaValue = (value: number, markPrice: number) => {
        if (isContractView) {
          return value ?
            currency(value, { precision: 4 }).format({ symbol: "" }) :
            "0";
        }

        return currency(value * markPrice).format();
      };

      const zeroDeltaValue = isContractView ? "0" : "$0";

      return [
        {
          title: "Instrument Name",
          align: "left",
          accessor: "instrument_name",
          Cell: ({ value }) => <div>{value}</div>,
        },
        {
          title: "Net Side",
          align: "left",
          accessor: "contracts",
          id: "net_side",
          Cell: ({ value }) => (
            <div
              style={{
                color:
                  // eslint-disable-next-line no-nested-ternary
                  value === 0 ? TEXT_COLORS.three : value > 0 ? COLORS.green.one : COLORS.red.one,
              }}
            >
              {/* eslint-disable-next-line no-nested-ternary */}
              {value === 0 ? <span style={{ color: TEXT_COLORS.three }}>-</span> : value > 0 ? "Long" : "Short"}
            </div>
          ),
        },
        {
          title: `Net Delta (${isContractView ? "Contracts" : "$"})`,
          align: "right",
          accessor: "netDelta",
          Cell: ({ value, row }) => (
            <Align align={"right"}>
              <div
                style={{
                  color: getDeltaColor(value),
                  fontWeight: 600,
                }}
              >
                {formatDeltaValue(value, row.original.markPrice)}
              </div>
            </Align>
          ),
          sortType: (a: any, b: any) => {
            const aValue = isContractView ?
              a.original.netDelta :
              a.original.netDelta * a.original.markPrice;
            const bValue = isContractView ?
              b.original.netDelta :
              b.original.netDelta * b.original.markPrice;

            return aValue > bValue ? 1 : -1;
          },
        },
        {
          title: `Aevo Delta (${isContractView ? "Contracts" : "$"})`,
          align: "right",
          accessor: "aevoDelta",
          Cell: ({ value, row }) => (
            <Align align={"right"}>
              {value ? (
                <div
                  style={{
                    color: getDeltaColor(value),
                  }}
                >
                  {formatDeltaValue(value, row.original.markPrice)}
                </div>
              ) : (
                <span style={{ color: TEXT_COLORS.three }}>{zeroDeltaValue}</span>
              )}
            </Align>
          ),
        },
        {
          title: `Binance Delta (${isContractView ? "Contracts" : "$"})`,
          align: "right",
          accessor: "binanceDelta",
          Cell: ({ value, row }) => (
            <Align align={"right"}>
              {value ? (
                <div
                  style={{
                    color: getDeltaColor(value),
                  }}
                >
                  {formatDeltaValue(value, row.original.markPrice)}
                </div>
              ) : (
                <span style={{ color: TEXT_COLORS.three }}>{zeroDeltaValue}</span>
              )}
            </Align>
          ),
        },
        {
          title: `Bybit Delta (${isContractView ? "Contracts" : "$"})`,
          align: "right",
          accessor: "bybitDelta",
          Cell: ({ value, row }) => (
            <Align align={"right"}>
              {value ? (
                <div
                  style={{
                    color: getDeltaColor(value),
                  }}
                >
                  {formatDeltaValue(value, row.original.markPrice)}
                </div>
              ) : (
                <span style={{ color: TEXT_COLORS.three }}>{zeroDeltaValue}</span>
              )}
            </Align>
          ),
        },
        {
          title: `Hyperliquid Delta (${isContractView ? "Contracts" : "$"})`,
          align: "right",
          accessor: "hyperliquidDelta",
          Cell: ({ value, row }) => (
            <Align align={"right"}>
              {value ? (
                <div
                  style={{
                    color: getDeltaColor(value),
                  }}
                >
                  {formatDeltaValue(value, row.original.markPrice)}
                </div>
              ) : (
                <span style={{ color: TEXT_COLORS.three }}>{zeroDeltaValue}</span>
              )}
            </Align>
          ),
        },
        {
          title: `Lighter Robinhood Delta (${isContractView ? "Contracts" : "$"})`,
          align: "right",
          accessor: "lighterDelta",
          Cell: ({ value, row }) => (
            <Align align={"right"}>
              {value ? (
                <div style={{ color: getDeltaColor(value) }}>
                  {formatDeltaValue(value, row.original.markPrice)}
                </div>
              ) : (
                <span style={{ color: TEXT_COLORS.three }}>{zeroDeltaValue}</span>
              )}
            </Align>
          ),
        },
        {
          title: `Lighter Mainnet Delta (${isContractView ? "Contracts" : "$"})`,
          align: "right",
          accessor: "lighterMainnetDelta",
          Cell: ({ value, row }) => (
            <Align align={"right"}>
              {value ? (
                <div style={{ color: getDeltaColor(value) }}>
                  {formatDeltaValue(value, row.original.markPrice)}
                </div>
              ) : (
                <span style={{ color: TEXT_COLORS.three }}>{zeroDeltaValue}</span>
              )}
            </Align>
          ),
        },
        {
          title: "Aevo Mark Price",
          align: "right",
          accessor: "aevoMarkPrice",
          Cell: ({ value }) => (
            <Align align={"right"}>
              {value ? (
                `$${Number(value).toLocaleString("en-US", { maximumSignificantDigits: 6, minimumSignificantDigits: 1 })}`
              ) : (
                <span style={{ color: TEXT_COLORS.three }}>-</span>
              )}
            </Align>
          ),
        },
        {
          title: "Binance Mark Price",
          align: "right",
          accessor: "binanceMarkPrice",
          Cell: ({ value }) => (
            <Align align={"right"}>
              {value ? (
                `$${Number(value).toLocaleString("en-US", { maximumSignificantDigits: 6, minimumSignificantDigits: 1 })}`
              ) : (
                <span style={{ color: TEXT_COLORS.three }}>-</span>
              )}
            </Align>
          ),
        },
        {
          title: "Bybit Mark Price",
          align: "right",
          accessor: "bybitMarkPrice",
          Cell: ({ value }) => (
            <Align align={"right"}>
              {value ? (
                `$${Number(value).toLocaleString("en-US", { maximumSignificantDigits: 6, minimumSignificantDigits: 1 })}`
              ) : (
                <span style={{ color: TEXT_COLORS.three }}>-</span>
              )}
            </Align>
          ),
        },
        {
          title: "Hyperliquid Mark Price",
          align: "right",
          accessor: "hyperliquidMarkPrice",
          Cell: ({ value }) => (
            <Align align={"right"}>
              {value ? (
                `$${Number(value).toLocaleString("en-US", { maximumSignificantDigits: 6, minimumSignificantDigits: 1 })}`
              ) : (
                <span style={{ color: TEXT_COLORS.three }}>-</span>
              )}
            </Align>
          ),
        },
        {
          title: "Lighter Robinhood Mark Price",
          align: "right",
          accessor: "lighterMarkPrice",
          Cell: ({ value }) => (
            <Align align={"right"}>
              {value ? (
                `$${Number(value).toLocaleString("en-US", { maximumSignificantDigits: 6, minimumSignificantDigits: 1 })}`
              ) : (
                <span style={{ color: TEXT_COLORS.three }}>-</span>
              )}
            </Align>
          ),
        },
        {
          title: "Lighter Mainnet Mark Price",
          align: "right",
          accessor: "lighterMainnetMarkPrice",
          Cell: ({ value }) => (
            <Align align={"right"}>
              {value ? (
                `$${Number(value).toLocaleString("en-US", { maximumSignificantDigits: 6, minimumSignificantDigits: 1 })}`
              ) : (
                <span style={{ color: TEXT_COLORS.three }}>-</span>
              )}
            </Align>
          ),
        },
        {
          title: "Unrealized PnL",
          align: "right",
          accessor: "pnl",
          Cell: ({ value }) => (
            <Align
              align="right"
              style={{
                color:
                  // eslint-disable-next-line no-nested-ternary
                  Number(value || 0) > 0
                    ? COLORS.green.one
                    : Number(value || 0) < 0
                    ? COLORS.red.one
                    : TEXT_COLORS.one,
              }}
            >
              {currency(Number(value)).format()}
            </Align>
          ),
        },
      ];
    },
    [isContractView]
  );

  const { getTableProps, getTableBodyProps, headerGroups, rows, prepareRow } = useTable(
    {
      columns: columns as any,
      data: memoizedPositions,
      defaultColumn: {
        Header: DefaultHeaderForColumn,
        Cell: DefaultCellForColumn,
      } as any,
      autoResetSortBy: false,
    },
    useSortBy
  );

  const positionsWithContracts = useMemo(
    () => memoizedPositions.filter((pos) => pos.contracts !== 0),
    [memoizedPositions]
  );

  return (
    <Collapse
      defaultActiveKey={["1"]}
      expandIconPosition="end"
      items={[
        {
          key: "1",
          label: <CollapseHeaderCount count={positionsWithContracts.length} showBadge title="Aggregated Positions" />,
          children: (
            <div style={{ position: "relative" }}>
              {isLoading && positionsWithContracts.length > 0 && (
                <div
                  style={{
                    position: "absolute",
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    backgroundColor: "rgba(0, 0, 0, 0.3)",
                    zIndex: 100,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <Spinner />
                </div>
              )}
              <MinifiedTableWrapper>
                {isLoading && positionsWithContracts.length === 0 ? (
                  <TableLoader isLoading />
                ) : (
                  <table {...getTableProps()}>
                    <thead
                      style={{
                        position: "sticky",
                        top: 0,
                        zIndex: 10,
                        backgroundColor: "#0a0a0a",
                      }}
                    >
                      {headerGroups.map((headerGroup) => (
                        // eslint-disable-next-line react/jsx-key
                        <tr {...headerGroup.getHeaderGroupProps()}>
                          {headerGroup.headers.map((column: any) => (
                            // eslint-disable-next-line react/jsx-key
                            <TableHeaderCell
                              {...column.getHeaderProps(column.getSortByToggleProps())}
                              style={{ width: column.width }}
                            >
                              {column.render("Header")}
                            </TableHeaderCell>
                          ))}
                        </tr>
                      ))}
                    </thead>
                    <tbody {...getTableBodyProps()}>
                      {rows.map((row) => {
                        prepareRow(row);
                        const { ...rowProps } = row.getRowProps();
                        return (
                          // eslint-disable-next-line react/jsx-key
                          <tr {...rowProps}>
                            {row.cells.map((cell) => (
                              // eslint-disable-next-line react/jsx-key
                              <td {...cell.getCellProps()}>{cell.render("Cell")}</td>
                            ))}
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                )}
              </MinifiedTableWrapper>
            </div>
          ),
        },
      ]}
    />
  );
}
